import { useEffect, useRef } from 'react';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  hideCancel?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmModal({ 
  isOpen, 
  title, 
  message, 
  confirmText = 'Confirm', 
  cancelText = 'Cancel', 
  hideCancel = false,
  onConfirm, 
  onCancel 
}: ConfirmModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      if (modalRef.current) {
        modalRef.current.focus();
      }
    } else {
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
      
      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length === 0) return;
        
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4">
      <div 
        ref={modalRef}
        tabIndex={-1}
        className="bg-[var(--background1)] rounded-lg shadow-xl w-full max-w-sm border border-[var(--border1)] flex flex-col focus:outline-none"
        role="dialog" 
        aria-modal="true" 
        aria-labelledby="confirm-modal-title"
      >
        <div className="p-5 border-b border-[var(--border1)]">
          <h2 id="confirm-modal-title" className="text-[16px] md:text-[17px] font-bold text-[var(--text1)]">{title}</h2>
        </div>
        
        <div className="p-5">
          <p className="text-[13px] md:text-[14px] text-[var(--text2)] leading-relaxed whitespace-pre-wrap">
            {message}
          </p>
        </div>

        <div className="p-4 bg-[var(--background2)] border-t border-[var(--border1)] flex justify-end gap-3 rounded-b-lg">
          {!hideCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 text-[12px] md:text-[13px] font-medium text-[var(--text2)] bg-transparent hover:bg-[var(--background3)] rounded transition"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-[12px] md:text-[13px] font-medium text-[var(--actionPrimaryText)] bg-[var(--actionPrimaryBackground)] hover:bg-[var(--actionPrimaryBackgroundHover)] rounded transition"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
